import type { FormTreeQuestion } from '../../components/oasis/types';
import { flattenQuestionTree } from '../../components/oasis/walkQuestionTree';

/** One selectable template target: a text question on the form being browsed. */
export interface TextFieldOption {
  qid: string;
  label: string;
  /** Key of the `schema_data` section the question was found in. */
  section: string;
}

/**
 * Every text question on a form, as options for the template target picker.
 *
 * Nested subitems are included: a question tucked under a parent's answer is
 * still a box somebody types a narrative into, and the ones worth templating
 * are often exactly there.
 *
 * Only the schema type is filtered on. Whether a text question renders as a
 * one-line input or a large box is decided by the form's layout rather than by
 * the schema, so filtering on that here would hide fields an agency may well
 * want a template for. Which questions are worth templating is the admin's
 * judgement, and choosing one is how they express it.
 *
 * A question id can appear in more than one section of a form, and the picker
 * stores the id alone, so a repeat would be an option that does the same thing
 * twice. The first occurrence wins, which also fixes which section it is filed
 * under.
 *
 * Options come back grouped by section and alphabetical within one, rather than
 * alphabetical across the whole form. An admin looking for a field remembers
 * roughly where on the form it sits, so the section is the coarse filter they
 * already have in mind. `sectionOrder` puts those groups in the order the form
 * itself presents them; without it the schema's own key order is used.
 */
export function collectTextFieldOptions(
  schemaData: Record<string, FormTreeQuestion[]>,
  sectionOrder?: readonly string[]
): TextFieldOption[] {
  const seen = new Set<string>();
  const bySection = new Map<string, TextFieldOption[]>();

  const orderedKeys = [
    ...(sectionOrder ?? []).filter(key => key in schemaData),
    ...Object.keys(schemaData).filter(key => !sectionOrder?.includes(key)),
  ];

  for (const section of orderedKeys) {
    const questions = schemaData[section];
    if (!Array.isArray(questions)) continue;
    for (const question of flattenQuestionTree(questions)) {
      if (question.type !== 'text') continue;
      if (seen.has(question.qid)) continue;
      seen.add(question.qid);
      const forSection = bySection.get(section);
      const option = {
        qid: question.qid,
        label: question.question || question.qid,
        section,
      };
      if (forSection) {
        forSection.push(option);
      } else {
        bySection.set(section, [option]);
      }
    }
  }

  return [...bySection.values()].flatMap(group =>
    group.sort((a, b) => a.label.localeCompare(b.label))
  );
}

/** Words worth matching on, from either a query or an option. */
function searchTokens(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

/**
 * The options matching what the admin has typed, searched by question id as
 * well as prompt.
 *
 * A form can carry dozens of text questions, and an admin arrives knowing
 * either the wording they saw on the form or the question id they were given,
 * so both have to be searchable. The id is matched as well as the prompt
 * because an id is what a support conversation or a spec names, and it is what
 * the saved template is actually keyed to.
 *
 * Every token in the query has to match, in any order, so "goal prog" finds
 * "Progress toward goals" without the admin having to recall the word order.
 * Tokens match at the start of a word rather than anywhere inside one: typing
 * "care" should not drag in every question containing "medicare".
 *
 * Splitting on non-alphanumerics is what makes an id searchable in pieces:
 * `t_care_a4b243` becomes `t`, `care`, `a4b243`, so both "care" and the hash
 * fragment find it.
 */
export function filterTextFieldOptions(
  options: TextFieldOption[],
  query: string
): TextFieldOption[] {
  const wanted = searchTokens(query);
  if (wanted.length === 0) return options;

  return options.filter(option => {
    const candidates = searchTokens(`${option.qid} ${option.label}`);
    return wanted.every(token =>
      candidates.some(candidate => candidate.startsWith(token))
    );
  });
}
